import { useQuery, useMutation } from '@apollo/client';
import * as AuthQuery from 'graphql/auth.graphql';
import * as OrganizationQuery from 'graphql/organization.graphql';
import * as PaymentQuery from 'graphql/payment.graphql';
import useUserSession from './useUserSession';
import { GetOrganization } from 'api/data/user/types';
import { Terminal } from '~/api/data/payment/types';

const usePermittedSessionPreferences = (spaceSlug: string) => {
  const { data: userSession, refetch: refetchSession } = useUserSession();
  const { data: organizationData } = useQuery<GetOrganization>(OrganizationQuery.GetOrganization, {
    variables: { currentSpace: spaceSlug },
    skip: !spaceSlug || !userSession,
  });

  const organization = organizationData?.organization;
  const { data: dataTerminals, refetch: refetchTerminals } = useQuery<{ paymentTerminals: Terminal[] }>(
    PaymentQuery.PaymentTerminals,
    {
      variables: { currentSpace: spaceSlug },
      skip: !spaceSlug || !userSession || !organization,
    },
  );

  const sessionQuery = { query: AuthQuery.Session };
  const terminalQuery = {
    query: PaymentQuery.PaymentTerminals,
    variables: { currentSpace: spaceSlug },
  };

  const [setSessionTerminal, { loading: sessionTerminalLoading, error: sessionTerminalError }] = useMutation(
    AuthQuery.SetSessionTerminal,
    { refetchQueries: [terminalQuery, sessionQuery] },
  );

  const sessionPreferences = userSession?.session.sessionPreferences;
  const paymentTerminals = dataTerminals?.paymentTerminals ?? [];
  const connectedPaymentTerminal =
    paymentTerminals.find(terminal => terminal.id === sessionPreferences?.connectedPaymentTerminal?.id) || null;

  const isInWebView = !!window.ReactNativeWebView;
  const hasInPersonPaymentsPermission = organization?.hasPaymentTerminals || isInWebView;

  const refetchSessionAndTerminals = async () => {
    await refetchTerminals();
    await refetchSession();
  };

  return {
    canAcceptInPersonPayments: Boolean(hasInPersonPaymentsPermission && sessionPreferences?.acceptInPersonPayments),
    acceptInPersonPayments: !!sessionPreferences?.acceptInPersonPayments,
    hasInPersonPaymentsPermission,
    acceptCashAndCheckPayments: !!sessionPreferences?.acceptCashAndCheckPayments,
    paymentTerminals,
    connectedPaymentTerminal: connectedPaymentTerminal,
    cashierName: sessionPreferences?.cashierName,
    currentUser: userSession?.session?.currentUser,
    setSessionTerminal,
    sessionTerminalLoading,
    sessionTerminalError,
    refetchSessionAndTerminals,
  };
};

export default usePermittedSessionPreferences;
